/* eslint-disable no-restricted-imports */
import { useEffect, useState } from 'react';

import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Container,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import {
  AccessTime,
  ArrowForward,
  Build,
  CalendarToday,
  Camera,
  ClearAll,
  Computer,
  DesktopWindows,
  ExpandLess,
  ExpandMore,
  Headset,
  Person,
  Search,
  Smartphone,
  Tablet,
  TuneOutlined,
  Watch,
} from '@material-ui/icons';

import { Autocomplete } from '@mui/material';
import Pagination from '@mui/material/Pagination';
import { FormatMoney } from 'components';
import { UseDebounce } from 'hooks/useDebounce';
import { useQuery } from 'hooks/useQuery';
import { budgetService } from 'services/budget';

import { processTypes } from '../../constants/processTypes';
import { productTypes } from '../../constants/productTypes';
import { statusOptions } from '../../constants/statusOptions';
import { useBudget } from '../../context/BudgetContext';
import { getStatusColor } from '../../utils/statusColors';
import BudgetListSkeleton from './BudgetListSkeleton';
import FilterSkeleton from './FilterSkeleton';
import { useStyles } from './styles';

export default function PendingList() {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const {
    handleSelectBudget: onSelectBudget,
    budgets,
    total,
    totalPages,
    budgetFilters,
    updateBudgetFilters,
    loadingBudgets,
  } = useBudget();

  const { debounce } = UseDebounce(800, false);

  const [modelsData, , loadingModels] = useQuery(() => budgetService.getBudgetModels(), []);

  const productModels = [
    { id: 'All', modelo: 'Todos', produto: '' },
    ...(modelsData || []).map((item) => ({
      id: item.modelo,
      modelo: item.modelo,
      produto: item.produto,
    })),
  ];

  const [searchTerm, setSearchTerm] = useState('');
  const [localDates, setLocalDates] = useState({
    startDate: '',
    endDate: '',
  });
  const [filtersExpanded, setFiltersExpanded] = useState(() => {
    const saved = localStorage.getItem('budgetFiltersExpanded');
    return saved !== null ? JSON.parse(saved) : true;
  });

  useEffect(() => {
    localStorage.setItem('budgetFiltersExpanded', JSON.stringify(filtersExpanded));
  }, [filtersExpanded]);

  const toggleFilters = () => {
    setFiltersExpanded(!filtersExpanded);
  };

  const hasActiveFilters = () => {
    return (
      budgetFilters.process ||
      budgetFilters.productType ||
      budgetFilters.productModel ||
      budgetFilters.status ||
      budgetFilters.dateStart ||
      budgetFilters.dateEnd ||
      searchTerm
    );
  };

  const getStatusInfo = (statusId) => {
    const statusOption = statusOptions.find((s) => s.id == statusId);

    const color = getStatusColor(statusId);

    return {
      color,
      label: statusOption?.description || 'Indefinido',
    };
  };

  const getProductIcon = (productType) => {
    const type = productType?.toUpperCase() || '';

    if (type.includes('TELEFONE') || type.includes('CELULAR') || type.includes('MOVEL')) {
      return <Smartphone className={classes.productIcon} />;
    }
    if (type.includes('TABLET')) {
      return <Tablet className={classes.productIcon} />;
    }
    if (type.includes('NOTEBOOK')) {
      return <Computer className={classes.productIcon} />;
    }
    if (type.includes('RELÓGIO') || type.includes('RELOGIO')) {
      return <Watch className={classes.productIcon} />;
    }
    if (type.includes('MONITOR')) {
      return <DesktopWindows className={classes.productIcon} />;
    }
    if (type.includes('CAMERA') || type.includes('CÂMERA')) {
      return <Camera className={classes.productIcon} />;
    }
    if (type.includes('FONE')) {
      return <Headset className={classes.productIcon} />;
    }
    if (
      type.includes('ACESSORIO') ||
      type.includes('ACESSÓRIO') ||
      type.includes('ANEL') ||
      type.includes('ÓCULOS')
    ) {
      return <Build className={classes.productIcon} />;
    }

    return <Build className={classes.productIcon} />;
  };

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchTerm(value);

    debounce(() => {
      updateBudgetFilters({ search: value, page: 1 });
    });
  };

  const handleDateChange = (dateType, value) => {
    setLocalDates((prev) => ({
      ...prev,
      [dateType === 'start' ? 'startDate' : 'endDate']: value,
    }));

    debounce(() => {
      if (dateType === 'start') {
        updateBudgetFilters({ dateStart: value, page: 1 });
      } else {
        updateBudgetFilters({ dateEnd: value, page: 1 });
      }
    });
  };

  const handlePageChange = (_event, value) => {
    updateBudgetFilters({ page: value });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const clearFilters = () => {
    setSearchTerm('');
    setLocalDates({
      startDate: '',
      endDate: '',
    });
    updateBudgetFilters({
      search: '',
      process: '',
      productType: '',
      productModel: '',
      status: '',
      dateStart: '',
      dateEnd: '',
      page: 1,
    });
  };

  const calculateTAT = (createdAt, updatedAt) => {
    if (!createdAt) return null;

    const created = new Date(createdAt || updatedAt);
    const now = new Date();
    const diffMs = now - created;

    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

    if (days > 0) {
      return `${days}d ${hours}h`;
    } else if (hours > 0) {
      return `${hours}h`;
    } else {
      const minutes = Math.floor(diffMs / (1000 * 60));
      return `${minutes}m`;
    }
  };

  const getTATColor = (createdAt) => {
    if (!createdAt) return '#9e9e9e';

    const created = new Date(createdAt);
    const now = new Date();
    const diffDays = Math.floor((now - created) / (1000 * 60 * 60 * 24));

    if (diffDays >= 7) return '#f44336';
    if (diffDays >= 3) return '#ff9800';
    return '#4caf50';
  };

  return (
    <div className={classes.root}>
      <Box className={classes.header}>
        <Container maxWidth='lg' style={{ padding: '16px' }}>
          <Box>
            <Typography variant='h5' className={classes.title} style={{ fontSize: '1.5rem' }}>
              Orçamentos
            </Typography>
            <Typography variant='body2' className={classes.subtitle}>
              Gerencie suas ordens de serviço
            </Typography>
          </Box>
        </Container>
      </Box>

      <Container maxWidth='lg'>
        <Box className={classes.searchSection}>
          <TextField
            fullWidth
            variant='outlined'
            placeholder='Buscar por número de ordem, IMEI, CPF/CNPJ...'
            value={searchTerm}
            onChange={handleSearchChange}
            className={classes.searchField}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <Search color='action' />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Box className={classes.filterSection}>
          <Box
            display='flex'
            alignItems='center'
            justifyContent='space-between'
            onClick={toggleFilters}
            style={{ cursor: 'pointer' }}
          >
            <Box display='flex' alignItems='center' gap={1}>
              <TuneOutlined style={{ marginRight: 8, color: '#1976d2', fontSize: 24 }} />
              <Typography variant='subtitle1' style={{ fontWeight: 600, color: '#1976d2' }}>
                Filtros Avançados
              </Typography>
              {hasActiveFilters() && (
                <Chip
                  label='Ativos'
                  size='small'
                  style={{
                    backgroundColor: '#1976d2',
                    color: 'white',
                    height: 20,
                    fontSize: '0.65rem',
                  }}
                />
              )}
            </Box>
            <IconButton size='small' onClick={toggleFilters}>
              {filtersExpanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>

          <Collapse in={filtersExpanded} timeout='auto'>
            {loadingModels ? (
              <FilterSkeleton />
            ) : (
              <Box marginTop={2}>
                <Button
                  variant='outlined'
                  color='secondary'
                  size='small'
                  startIcon={<ClearAll />}
                  onClick={clearFilters}
                  style={{ borderRadius: 8, marginBottom: 16 }}
                  fullWidth
                >
                  Limpar Filtros
                </Button>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Autocomplete
                      size='small'
                      options={processTypes}
                      getOptionLabel={(option) => option.description}
                      value={
                        processTypes.find((p) => p.id === budgetFilters.process) || processTypes[0]
                      }
                      onChange={(_event, newValue) => {
                        updateBudgetFilters({
                          process: newValue?.id === 'All' ? '' : newValue?.id || '',
                          page: 1,
                        });
                      }}
                      renderInput={(params) => (
                        <TextField {...params} label='Processo' variant='outlined' size='small' />
                      )}
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Autocomplete
                      size='small'
                      options={productTypes}
                      getOptionLabel={(option) => option.description}
                      value={
                        productTypes.find((p) => p.id === budgetFilters.productType) ||
                        productTypes[0]
                      }
                      onChange={(_event, newValue) => {
                        updateBudgetFilters({
                          productType: newValue?.id === 'All' ? '' : newValue?.id || '',
                          page: 1,
                        });
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label='Tipo de Produto'
                          variant='outlined'
                          size='small'
                        />
                      )}
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Autocomplete
                      size='small'
                      options={productModels}
                      getOptionLabel={(option) => option.modelo || ''}
                      value={
                        productModels.find((m) => m.id === budgetFilters.productModel) ||
                        productModels[0]
                      }
                      onChange={(_event, newValue) => {
                        updateBudgetFilters({
                          productModel: newValue?.id === 'All' ? '' : newValue?.id || '',
                          page: 1,
                        });
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label='Modelo do Produto'
                          variant='outlined'
                          size='small'
                        />
                      )}
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Autocomplete
                      size='small'
                      options={statusOptions}
                      getOptionLabel={(option) => option.description}
                      value={
                        statusOptions.find((s) => s.id === budgetFilters.status) || statusOptions[0]
                      }
                      onChange={(_event, newValue) => {
                        updateBudgetFilters({
                          status: newValue?.id === 'All' ? '' : newValue?.id || '',
                          page: 1,
                        });
                      }}
                      renderInput={(params) => (
                        <TextField {...params} label='Status' variant='outlined' size='small' />
                      )}
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <TextField
                      label='Data Inicial'
                      type='date'
                      variant='outlined'
                      size='small'
                      InputLabelProps={{ shrink: true }}
                      value={localDates.startDate}
                      onChange={(e) => handleDateChange('start', e.target.value)}
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <TextField
                      label='Data Final'
                      type='date'
                      variant='outlined'
                      size='small'
                      InputLabelProps={{ shrink: true }}
                      value={localDates.endDate}
                      onChange={(e) => handleDateChange('end', e.target.value)}
                      fullWidth
                    />
                  </Grid>
                </Grid>

                <Box marginTop={3} paddingTop={2} borderTop='1px solid #e0e0e0'>
                  <Typography variant='body2' style={{ color: '#666', fontWeight: 500 }}>
                    <strong style={{ color: '#1976d2' }}>{budgets.length}</strong> de{' '}
                    <strong style={{ color: '#1976d2' }}>{total}</strong> ordens
                  </Typography>
                  <Typography
                    variant='caption'
                    style={{ color: '#999', display: 'block', marginTop: 4 }}
                  >
                    Página {budgetFilters.page} de {totalPages || 1}
                  </Typography>
                </Box>
              </Box>
            )}
          </Collapse>
        </Box>

        {loadingBudgets ? (
          <BudgetListSkeleton count={budgetFilters.rowsPerPage} />
        ) : (
          <Box display='flex' flexDirection='column' gap={2}>
            {budgets.map((item) => {
              const statusInfo = getStatusInfo(item.budget?.status);
              const tat = calculateTAT(item.createdAt, item.updatedAt);
              const tatColor = getTATColor(item.createdAt);

              return (
                <Card
                  key={item.budget?.id}
                  className={classes.budgetCard}
                  onClick={() => onSelectBudget(item)}
                >
                  <CardContent style={{ padding: 16 }}>
                    <Box>
                      <Box
                        display='flex'
                        alignItems='flex-start'
                        justifyContent='space-between'
                        marginBottom={2}
                        flexWrap='wrap'
                        gap={1}
                      >
                        <Box flex={1} minWidth={150}>
                          <Typography variant='subtitle1' style={{ fontWeight: 600 }}>
                            OS: {item.serviceOrder}
                            {item.jobOrder && (
                              <Typography variant='caption' color='textSecondary' display='block'>
                                Job: {item.jobOrder}
                              </Typography>
                            )}
                          </Typography>
                          {tat && (
                            <Box display='flex' alignItems='center' gap={0.5} marginTop={0.5}>
                              <AccessTime style={{ fontSize: 14, color: tatColor }} />
                              <Typography
                                variant='caption'
                                style={{ fontWeight: 600, color: tatColor }}
                              >
                                TAT: {tat}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                        <Chip
                          label={statusInfo.label}
                          size='small'
                          className={classes.statusChip}
                          style={{
                            backgroundColor: `${statusInfo.color}20`,
                            color: statusInfo.color,
                            borderColor: statusInfo.color,
                            fontSize: '0.7rem',
                          }}
                        />
                      </Box>

                      <Grid container spacing={2} marginBottom={2}>
                        <Grid item xs={12} sm={6}>
                          <Box className={classes.clientInfo}>
                            {getProductIcon(item.product)}
                            <Box>
                              <Typography variant='body2' style={{ fontWeight: 600 }}>
                                {item.product}
                              </Typography>
                              <Typography variant='caption' color='textSecondary'>
                                {item.model}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <Box className={classes.clientInfo}>
                            <Person color='action' fontSize='small' />
                            <Box style={{ overflow: 'hidden' }}>
                              <Typography variant='body2' style={{ fontWeight: 600 }} noWrap>
                                {item.client?.name || 'N/A'}
                              </Typography>
                              <Typography variant='caption' color='textSecondary' noWrap>
                                {item.client?.cgc || 'N/A'}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <Box className={classes.clientInfo}>
                            <CalendarToday color='action' fontSize='small' />
                            <Box>
                              <Typography variant='caption' color='textSecondary'>
                                Última Modificação
                              </Typography>
                              <Typography variant='body2' style={{ fontWeight: 600 }}>
                                {new Date(item.updatedAt).toLocaleDateString('pt-BR')}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <Box>
                            <Typography variant='caption' color='textSecondary'>
                              Valor Total
                            </Typography>
                            <Typography variant='h6' className={classes.valueText}>
                              <FormatMoney>{item.budget?.totalValue || 0}</FormatMoney>
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>

                      <Box marginTop={2} paddingTop={2} borderTop='1px solid #f0f0f0'>
                        <Typography
                          variant='caption'
                          color='textSecondary'
                          display='block'
                          marginBottom={1}
                        >
                          <strong>Processo:</strong> {item.process}
                          {item.serial && (
                            <>
                              {' • '}
                              <strong>Serial/IMEI:</strong> {item.serial}
                            </>
                          )}
                          {item.budget?.discount > 0 && (
                            <>
                              {' • '}
                              <strong>Desconto:</strong> {item.budget.discount}%
                            </>
                          )}
                        </Typography>

                        <Box
                          display='flex'
                          alignItems='center'
                          justifyContent='flex-end'
                          className={classes.actionText}
                        >
                          <Typography variant='caption' style={{ fontWeight: 600, marginRight: 4 }}>
                            Ver Detalhes
                          </Typography>
                          <ArrowForward fontSize='small' />
                        </Box>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        )}

        {totalPages > 1 && (
          <Box className={classes.pagination}>
            <Pagination
              count={totalPages}
              page={budgetFilters.page}
              onChange={handlePageChange}
              color='primary'
              size={isMobile ? 'small' : isTablet ? 'medium' : 'large'}
              showFirstButton={!isMobile}
              showLastButton={!isMobile}
              siblingCount={isMobile ? 0 : 1}
              boundaryCount={isMobile ? 1 : 1}
            />
          </Box>
        )}
      </Container>
    </div>
  );
}
